'use client';
import http from '@/utils/httpClient';
import { toaster } from '@/utils/toast';
import { Action, Thunk, action, thunk } from 'easy-peasy';

// easy-peasy slice for the Harvest time-tracking integration. Mirrors the
// house pattern (interfaces -> action/thunk types -> implementation): thunks
// call the shared `http()` client, set state on success, and toast on error;
// mutating thunks resolve a sentinel string so pages can branch on the result.
// Registered in src/model/Models.ts as `HarvestModel`.

export interface HourTotals {
  week: number;
  month: number;
  allTime: number;
}

export interface HarvestEngagement {
  engagementId: string;
  jobPostId: string;
  jobTitle: string | null;
  advisorName: string | null;
  advisorCompany: string | null;
  status: string;
  hours: HourTotals;
}

export interface HarvestTimeEntry {
  harvestId: number;
  engagementId: string | null;
  spentDate: string;
  hours: number;
  notes: string | null;
  billable: boolean;
  isRunning: boolean;
  harvestUserId?: number;
}

export interface HarvestExpense {
  harvestId: number;
  engagementId: string | null;
  spentDate: string;
  totalCost: number;
  expenseCategoryName: string | null;
  notes: string | null;
  billable: boolean;
}

export interface TimerState {
  running: boolean;
  entry: HarvestTimeEntry | null;
  degraded?: boolean;
}

export interface WeeklyHours {
  weekStart: string;
  hours: number;
}

export interface JobSummary {
  totalHours: number;
  billableHours: number;
  expenseTotal: number;
  hoursByWeek: WeeklyHours[];
}

export interface HarvestSyncStateRow {
  resource: string;
  updatedSinceCursor: string | null;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lastFullSweepAt: string | null;
  lastError: string | null;
}

export interface AdminHealth {
  connection: {
    ok: boolean;
    accountName?: string | null;
    wantsTimestampTimers?: boolean | null;
    latencyMs?: number | null;
    error?: string;
  };
  syncState: HarvestSyncStateRow[];
  counts: {
    activeEngagements: number;
    failedOps: number;
    orphanEntries: number;
    invitedNotActive: number;
  };
}

export interface AdminEngagement {
  id: string;
  jobPostId: string;
  providerId: string;
  advisorId: string;
  status: string;
  harvestProjectId: number | null;
  lastError?: string | null;
}

/*--- STATE ---*/
export interface IHarvestModel {
  inviteStatus: string;
  engagements: HarvestEngagement[];
  timeEntries: HarvestTimeEntry[];
  expenses: HarvestExpense[];
  timer: TimerState | null;
  jobSummary: JobSummary | null;
  jobTimeEntries: HarvestTimeEntry[];
  jobExpenses: HarvestExpense[];
  adminHealth: AdminHealth | null;
  adminEngagements: AdminEngagement[];

  /*--- ACTION TYPE ---*/
  setInviteStatus: Action<IHarvestModel, string>;
  setEngagements: Action<IHarvestModel, HarvestEngagement[]>;
  setTimeEntries: Action<IHarvestModel, HarvestTimeEntry[]>;
  setExpenses: Action<IHarvestModel, HarvestExpense[]>;
  setTimer: Action<IHarvestModel, TimerState | null>;
  setJobSummary: Action<IHarvestModel, JobSummary | null>;
  setJobTimeEntries: Action<IHarvestModel, HarvestTimeEntry[]>;
  setJobExpenses: Action<IHarvestModel, HarvestExpense[]>;
  setAdminHealth: Action<IHarvestModel, AdminHealth | null>;
  setAdminEngagements: Action<IHarvestModel, AdminEngagement[]>;

  /*--- THUNK TYPE ---*/
  getMyEngagements: Thunk<IHarvestModel>;
  getMyTimeEntries: Thunk<IHarvestModel, { engagementId: string; from?: string; to?: string }>;
  getMyExpenses: Thunk<IHarvestModel, { engagementId: string; from?: string; to?: string }>;
  getTimer: Thunk<IHarvestModel>;
  startTimer: Thunk<IHarvestModel, { engagementId: string; notes?: string }>;
  stopTimer: Thunk<IHarvestModel>;
  getJobSummary: Thunk<IHarvestModel, string>;
  getJobTimeEntries: Thunk<IHarvestModel, string>;
  getJobExpenses: Thunk<IHarvestModel, string>;
  endEngagement: Thunk<IHarvestModel, string>;
  getAdminHealth: Thunk<IHarvestModel>;
  getAdminEngagements: Thunk<IHarvestModel, string | undefined>;
  retryOp: Thunk<IHarvestModel, string>;
  resyncAll: Thunk<IHarvestModel>;
}

const query = (params: Record<string, string | undefined>) => {
  const q = Object.entries(params)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`)
    .join('&');
  return q ? `?${q}` : '';
};

const HarvestModel: IHarvestModel = {
  inviteStatus: 'pending',
  engagements: [],
  timeEntries: [],
  expenses: [],
  timer: null,
  jobSummary: null,
  jobTimeEntries: [],
  jobExpenses: [],
  adminHealth: null,
  adminEngagements: [],

  /*--- ACTION ---*/
  setInviteStatus: action((state, val) => {
    state.inviteStatus = val;
  }),
  setEngagements: action((state, val) => {
    state.engagements = val;
  }),
  setTimeEntries: action((state, val) => {
    state.timeEntries = val;
  }),
  setExpenses: action((state, val) => {
    state.expenses = val;
  }),
  setTimer: action((state, val) => {
    state.timer = val;
  }),
  setJobSummary: action((state, val) => {
    state.jobSummary = val;
  }),
  setJobTimeEntries: action((state, val) => {
    state.jobTimeEntries = val;
  }),
  setJobExpenses: action((state, val) => {
    state.jobExpenses = val;
  }),
  setAdminHealth: action((state, val) => {
    state.adminHealth = val;
  }),
  setAdminEngagements: action((state, val) => {
    state.adminEngagements = val;
  }),

  /*--- THUNK ---*/
  getMyEngagements: thunk(async (actions) => {
    await http()
      .get('/harvest/me/engagements')
      .then((res) => {
        actions.setEngagements(res.data.engagements || []);
        actions.setInviteStatus(res.data.inviteStatus || 'pending');
      })
      .catch(() => toaster('error', 'Could not load your engagements'));
  }),

  getMyTimeEntries: thunk(async (actions, payload) => {
    await http()
      .get(
        `/harvest/me/time-entries${query({ engagementId: payload.engagementId, from: payload.from, to: payload.to })}`
      )
      .then((res) => actions.setTimeEntries(res.data.timeEntries || []))
      .catch(() => toaster('error', 'Could not load time entries'));
  }),

  getMyExpenses: thunk(async (actions, payload) => {
    await http()
      .get(
        `/harvest/me/expenses${query({ engagementId: payload.engagementId, from: payload.from, to: payload.to })}`
      )
      .then((res) => actions.setExpenses(res.data.expenses || []))
      .catch(() => toaster('error', 'Could not load expenses'));
  }),

  getTimer: thunk(async (actions) => {
    await http()
      .get('/harvest/me/timer')
      .then((res) => actions.setTimer(res.data))
      .catch(() => actions.setTimer(null));
  }),

  startTimer: thunk(async (actions, payload) => {
    return await http()
      .post('/harvest/me/timer/start', payload)
      .then((res) => {
        actions.setTimer(res.data);
        return 'started';
      })
      .catch((e) => {
        toaster('error', e?.response?.data?.error || 'Could not start the timer');
        return 'error';
      });
  }),

  stopTimer: thunk(async (actions) => {
    return await http()
      .post('/harvest/me/timer/stop')
      .then((res) => {
        actions.setTimer(res.data);
        return 'stopped';
      })
      .catch(() => {
        toaster('error', 'Could not stop the timer');
        return 'error';
      });
  }),

  getJobSummary: thunk(async (actions, jobPostId) => {
    await http()
      .get(`/harvest/job/${jobPostId}/summary`)
      .then((res) => actions.setJobSummary(res.data))
      .catch(() => toaster('error', 'Could not load job hours'));
  }),

  getJobTimeEntries: thunk(async (actions, jobPostId) => {
    await http()
      .get(`/harvest/job/${jobPostId}/time-entries`)
      .then((res) => actions.setJobTimeEntries(res.data.timeEntries || []))
      .catch(() => toaster('error', 'Could not load time entries'));
  }),

  getJobExpenses: thunk(async (actions, jobPostId) => {
    await http()
      .get(`/harvest/job/${jobPostId}/expenses`)
      .then((res) => actions.setJobExpenses(res.data.expenses || []))
      .catch(() => toaster('error', 'Could not load expenses'));
  }),

  endEngagement: thunk(async (_actions, engagementId) => {
    return await http()
      .post(`/harvest/engagement/${engagementId}/end`)
      .then(() => {
        toaster('success', 'Engagement ended');
        return 'ended';
      })
      .catch(() => {
        toaster('error', 'Could not end the engagement');
        return 'error';
      });
  }),

  getAdminHealth: thunk(async (actions) => {
    await http()
      .get('/harvest/admin/health')
      .then((res) => actions.setAdminHealth(res.data))
      .catch(() => toaster('error', 'Could not load Harvest health'));
  }),

  getAdminEngagements: thunk(async (actions, status) => {
    await http()
      .get(`/harvest/admin/engagements${status ? `?status=${encodeURIComponent(status)}` : ''}`)
      .then((res) => actions.setAdminEngagements(res.data.engagements || []))
      .catch(() => toaster('error', 'Could not load engagements'));
  }),

  retryOp: thunk(async (_actions, opId) => {
    return await http()
      .post(`/harvest/admin/ops/${opId}/retry`)
      .then(() => {
        toaster('success', 'Operation re-queued');
        return 'retried';
      })
      .catch(() => {
        toaster('error', 'Could not retry the operation');
        return 'error';
      });
  }),

  resyncAll: thunk(async () => {
    return await http()
      .post('/harvest/admin/resync')
      .then(() => {
        toaster('success', 'Full resync scheduled');
        return 'resynced';
      })
      .catch(() => {
        toaster('error', 'Could not schedule resync');
        return 'error';
      });
  }),
};

export default HarvestModel;
